import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";

const EquipmentContext = createContext();
export const useEquipment = () => useContext(EquipmentContext);

export const EquipmentProvider = ({ children }) => {
  const [equipmentList, setEquipmentList] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);

  // 1. FETCH EQUIPMENT
  const fetchEquipment = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token || token.startsWith("prototype-jwt-token")) return;
      const res = await api.get("/equipment");
      setEquipmentList(res.data);
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      console.error("Error fetching equipment:", err);
    }
  };

  useEffect(() => { fetchEquipment(); }, []);

  // 2. GETTERS
  const getEquipment = (id) => equipmentList.find(eq => String(eq.id) === String(id));

  // 3. ACTIONS
  const addEquipment = async (data) => {
    try {
        const res = await api.post("/equipment", data);
        setEquipmentList(prev => [res.data, ...prev]);
    } catch(e) { toast.error("Failed to add"); }
  };

  const updateEquipment = async (id, data) => {
    try {
        await api.put(`/equipment/${id}`, data);
        setEquipmentList(prev => prev.map(eq => String(eq.id) === String(id) ? { ...eq, ...data } : eq));
    } catch(e) { toast.error("Failed to update"); }
  };

  const deleteEquipment = async (id) => {
    try {
        await api.delete(`/equipment/${id}`);
        setEquipmentList(prev => prev.filter(eq => String(eq.id) !== String(id)));
    } catch(e) { toast.error("Failed to delete"); }
  };

  // 🟢 4. LOG USAGE (Connected to Backend)
  const logEquipmentUsage = async (data) => {
    try {
        await api.post("/equipment/log", data);
        
        // Optimistic Update
        setEquipmentList(prev => prev.map(eq => {
            if(String(eq.id) === String(data.equipmentId)) {
                return { 
                    ...eq, 
                    status: 'In Use', 
                    currentProjectId: data.projectId,
                    totalRunningHours: Number(eq.totalRunningHours) + Number(data.hours) 
                };
            }
            return eq;
        }));
        
        return { success: true, message: "Logged Successfully" };
    } catch (err) {
        return { success: false, message: "Backend Error: " + err.message };
    }
  };

  const toggleMaintenance = async (id, isStarting) => {
      // Simple status update
      const status = isStarting ? "Under Maintenance" : "Available";
      await updateEquipment(id, { status });
  };

  const releaseEquipment = async (id) => {
      // Reset status
      await updateEquipment(id, { status: "Available", currentProjectId: null });
  };

  return (
    <EquipmentContext.Provider value={{
      equipmentList,
      getEquipment,
      logEquipmentUsage,
      toggleMaintenance,
      releaseEquipment,
      addEquipment,
      updateEquipment,
      deleteEquipment
    }}>
      {children}
    </EquipmentContext.Provider>
  );
};